// Tailoring measurement entry — slash shorthand -> quarter fractions.
//
// Counter staff type fast on the numpad, so trailing slashes stand in for the
// common quarter steps (sawa / aadha / paun):
//
//   40/    -> 40 ¼   (40.25)
//   40//   -> 40 ½   (40.5)
//   40///  -> 40 ¾   (40.75)
//
// Four or more slashes clamp to ¾ — eighths ("sut") are deliberately not
// supported yet, so a slipped keystroke rounds to the nearest quarter instead
// of silently inventing a finer measurement.
//
// Decimals (40.5), explicit fractions (40 1/2, 40-1/2) and plain integers all
// parse too, so nothing a tailor already knows how to type is rejected.

// Quarter step -> display glyph. Keys are the fractional remainder ×100 so we
// can look them up without float comparisons.
const QUARTER_GLYPH = { 25: '¼', 50: '½', 75: '¾' };

const QUARTER_PARTS = { 25: [1, 4], 50: [1, 2], 75: [3, 4] };

/** Round to the nearest quarter. */
const toQuarter = (n) => Math.round(n * 4) / 4;

/**
 * Parse a typed measurement into a number.
 *
 * Returns `null` for empty/unparseable input so callers can tell "nothing
 * entered" apart from a real 0.
 *
 * @param {string|number} raw
 * @returns {number|null}
 */
export function parseMeasurement(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? toQuarter(raw) : null;

  let s = String(raw).trim();
  if (!s) return null;

  // Drop any inch marks the user (or a previous format pass) left behind.
  s = s.replace(/["″'']/g, '').trim();
  if (!s) return null;

  // Unicode fraction glyphs — these come back in on re-focus after we format.
  s = s
    .replace(/¼/g, ' 1/4')
    .replace(/½/g, ' 1/2')
    .replace(/¾/g, ' 3/4')
    .trim();

  // --- Slash shorthand: digits followed by only slashes (40, 40/, 40//) ---
  const shorthand = s.match(/^(\d+(?:\.\d+)?)\s*(\/+)$/);
  if (shorthand) {
    const whole = Number(shorthand[1]);
    if (!Number.isFinite(whole)) return null;
    // 1 slash = ¼, 2 = ½, 3+ = ¾.
    const steps = Math.min(3, shorthand[2].length);
    return toQuarter(whole + steps * 0.25);
  }

  // --- Explicit fraction: "40 1/2", "40-1/2", "40 1 / 2", or bare "1/2" ---
  const explicit = s.match(/^(?:(\d+(?:\.\d+)?)\s*[-\s]\s*)?(\d+)\s*\/\s*(\d+)$/);
  if (explicit) {
    const whole = explicit[1] ? Number(explicit[1]) : 0;
    const numerator = Number(explicit[2]);
    const denominator = Number(explicit[3]);
    if (!denominator || !Number.isFinite(whole) || !Number.isFinite(numerator)) return null;
    return toQuarter(whole + numerator / denominator);
  }

  // --- Plain number / decimal (a bare ".5" counts as a half) ---
  const plain = s.match(/^\d*\.?\d+$/);
  if (plain) {
    const n = Number(s);
    return Number.isFinite(n) ? toQuarter(n) : null;
  }

  return null;
}

/**
 * Format a number as tailoring shorthand: "40", "40 ½".
 *
 * @param {number|null} value
 * @param {object}  [opts]
 * @param {boolean} [opts.inchMark]  append the ″ inch mark
 * @returns {string}
 */
export function formatMeasurement(value, { inchMark = false } = {}) {
  if (value == null || value === '' || !Number.isFinite(Number(value))) return '';
  const n = toQuarter(Number(value));
  const whole = Math.floor(n);
  const cents = Math.round((n - whole) * 100);
  const glyph = QUARTER_GLYPH[cents];
  const suffix = inchMark ? '″' : '';
  if (!glyph) return `${whole}${suffix}`;
  // Drop a leading 0 so a bare half reads "½" rather than "0 ½".
  return whole === 0 ? `${glyph}${suffix}` : `${whole} ${glyph}${suffix}`;
}

/**
 * Split a value into render parts, for typographic fraction markup
 * (superscript numerator / subscript denominator).
 *
 * @param {number|null} value
 * @returns {{whole: number, numerator: number|null, denominator: number|null}|null}
 */
export function measurementParts(value) {
  if (value == null || value === '' || !Number.isFinite(Number(value))) return null;
  const n = toQuarter(Number(value));
  const whole = Math.floor(n);
  const cents = Math.round((n - whole) * 100);
  const parts = QUARTER_PARTS[cents];
  return {
    whole,
    numerator: parts ? parts[0] : null,
    denominator: parts ? parts[1] : null,
  };
}

/**
 * True when `raw` is mid-entry shorthand we should leave alone while typing
 * (e.g. "40/" before the user has finished). Used to avoid reformatting on
 * every keystroke.
 */
export function isPartialEntry(raw) {
  return /\/\s*$/.test(String(raw ?? ''));
}
